import { useState, useCallback, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { LockedBanner } from "../common/LockedBanner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, AlertCircle, ExternalLink, Plus, Trash2, ChevronDown, ChevronRight, Info } from "lucide-react";
import { VoiceButton } from "@/components/workflow/common/VoiceButton";
import { ProgressCard } from "../common/ProgressCard";
import { useWizard, POLICY_TOPICS, type PolicyTarget, type PolicyTopicData } from "@/contexts/WizardContext";
import { getTopicStatus } from "../common/utils";
import { DocumentUploadSection } from "../common/DocumentUploadSection";
import { DescriptionForm } from "./DescriptionForm";
import { CertificationsForm } from "./CertificationsForm";
import { SectionCard } from "@/components/workflow/common/SectionCard";
import { CardPanelHeader } from "@/components/workflow/common/CardPanelHeader";

const sectionTitleClassName = "text-sm font-bold leading-5 text-slate-700";
const sectionCopyClassName = "text-sm font-normal leading-5 text-slate-500";
const fieldInputClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-0 focus-visible:border-slate-300";
const fieldSelectClassName = "h-11 rounded-2xl border-slate-200 bg-slate-50 px-3 text-base text-slate-900 shadow-none data-[placeholder]:text-slate-400 focus:ring-0";
const toggleButtonClassName = "h-10 rounded-2xl px-4 text-sm font-semibold shadow-none";

export function SustainabilitySection({ onClose }: { onClose: () => void }) {
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string>("");
  const {
    descriptionOfActivities,
    setDescriptionOfActivities,
    generalInfo,
    updateGeneralInfo,
    hasSustainabilityPolicies,
    setHasSustainabilityPolicies,
    sustainabilityPolicyDescription,
    setSustainabilityPolicyDescription,
    sustainabilityPolicyDocuments,
    setSustainabilityPolicyDocuments,
    policyTopics,
    setPolicyTopics,
    saveStatus,
    forceSave,
    isLocked,
    isReadOnly,
    sustainabilityCardCompletion,
  } = useWizard();
  const disabled = isLocked || isReadOnly;


  const completedTopics = policyTopics.filter((topic) => getTopicStatus(topic) === "complete").length;
  const availableTopics = useMemo(
    () => POLICY_TOPICS.filter((topic) => !policyTopics.some((existing) => existing.id === topic.id)),
    [policyTopics]
  );

  useEffect(() => {
    if (availableTopics.length === 0) {
      if (selectedTopicId !== "") setSelectedTopicId("");
      return;
    }

    const selectedStillAvailable = availableTopics.some((topic) => topic.id === selectedTopicId);
    if (!selectedStillAvailable) {
      setSelectedTopicId(availableTopics[0].id);
    }
  }, [availableTopics, selectedTopicId]);

  const updateTopic = useCallback((topicId: string, updates: Partial<PolicyTopicData>) => {
    setPolicyTopics((prev) => prev.map((topic) => topic.id === topicId ? { ...topic, ...updates } : topic));
  }, [setPolicyTopics]);

  const addTarget = useCallback((topicId: string) => {
    const newTarget: PolicyTarget = {
      id: `target-${Date.now()}`,
      targetName: "",
      metric: "",
      targetYear: new Date().getFullYear() + 1,
      targetValue: 0,
    };
    setPolicyTopics((prev) => prev.map((topic) => topic.id === topicId ? { ...topic, targets: [...(topic.targets || []), newTarget] } : topic));
  }, [setPolicyTopics]);

  const updateTarget = useCallback((topicId: string, targetId: string, updates: Partial<PolicyTarget>) => {
    setPolicyTopics((prev) => prev.map((topic) =>
      topic.id === topicId ? { ...topic, targets: topic.targets?.map((target) => target.id === targetId ? { ...target, ...updates } : target) } : topic
    ));
  }, [setPolicyTopics]);

  const removeTarget = useCallback((topicId: string, targetId: string) => {
    setPolicyTopics((prev) => prev.map((topic) =>
      topic.id === topicId ? { ...topic, targets: topic.targets?.filter((target) => target.id !== targetId) } : topic
    ));
  }, [setPolicyTopics]);

  const addTopic = useCallback(() => {
    if (!selectedTopicId) return;
    const selectedTopic = POLICY_TOPICS.find((topic) => topic.id === selectedTopicId);
    if (!selectedTopic) return;

    const newTopic: PolicyTopicData = {
      id: selectedTopic.id,
      name: selectedTopic.name,
      policyExists: "yes",
      publiclyAvailable: null,
      hasTargets: null,
      publicUrl: "",
      targets: [],
      notes: "",
      internalOwner: "",
      uploadedPolicyFiles: [],
    };

    setPolicyTopics((prev) => [...prev, newTopic]);
    setExpandedTopicId(selectedTopic.id);
  }, [selectedTopicId, setPolicyTopics]);

  const removeTopic = useCallback((topicId: string) => {
    setPolicyTopics((prev) => prev.filter((topic) => topic.id !== topicId));
    setExpandedTopicId((prev) => prev === topicId ? null : prev);
  }, [setPolicyTopics]);

  return (
    <section className="space-y-6" data-testid="card-sustainability-practices">
      <CardPanelHeader
        title="Policies & targets"
        description={"Tell us about the sustainability policies your company has in place, if any. Only add the topics that apply"}
        onClose={onClose}
        onSave={forceSave}
        saveStatus={saveStatus}
        disabled={disabled}
        tag={"General"}
      />

      <SectionCard collapsible={false}>
        <div className="space-y-8">
          {disabled && <LockedBanner />}

          <DescriptionForm
            value={descriptionOfActivities}
            onChange={setDescriptionOfActivities}
            isLocked={disabled}
          />

          <CertificationsForm
            generalInfo={generalInfo}
            updateGeneralInfo={updateGeneralInfo}
            isLocked={disabled}
          />

          <div className="rounded-3xl border border-[#64748B]/10 bg-[#64748B]/5 p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div>
                <h3 className="text-base font-bold leading-6 text-slate-800">{"Sustainability Policies"}</h3>
                <p className={sectionCopyClassName}>Does your business have any formal sustainability policies in place?</p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-bold leading-4 ${hasSustainabilityPolicies === "no" ? "text-slate-500" : "text-slate-400"}`}>{"No"}</span>
                <Switch
                  checked={hasSustainabilityPolicies === "yes"}
                  onCheckedChange={(checked) => setHasSustainabilityPolicies(checked ? "yes" : "no")}
                  disabled={disabled}
                  data-testid="button-has-policies-toggle"
                  className="data-[state=checked]:bg-[#00444C]"
                />
                <span className={`text-xs font-bold leading-4 ${hasSustainabilityPolicies === "yes" ? "text-slate-500" : "text-slate-400"}`}>{"Yes"}</span>
              </div>
            </div>
          </div>

          {hasSustainabilityPolicies === "no" && (
            <div className="flex items-start gap-3 rounded-3xl border border-slate-200 bg-slate-50 p-4">
              <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">{"No sustainability policies are currently in place. You can update this at any time if policies are established."}</p>
            </div>
          )}

          {hasSustainabilityPolicies === "yes" && (
            <div className="space-y-8">
              <div className="space-y-4">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div>
                    <h4 className={sectionTitleClassName}>{"Policy Topics"}</h4>
                    <p className={sectionCopyClassName}>Only select topics where your business has an active policy. You do not need to select every topic.</p>
                  </div>
                  <Badge variant="outline" className="w-fit rounded-2xl border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-500 shadow-none" data-testid="badge-topics-progress">
                    {`${completedTopics} / ${policyTopics.length} topics reviewed`}
                  </Badge>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4">
                  <Label className="mb-2 block text-sm font-medium text-slate-700">{"Add Topic"}</Label>
                  <div className="flex flex-col gap-2 md:flex-row">
                    <Select value={selectedTopicId} onValueChange={setSelectedTopicId} disabled={disabled || availableTopics.length === 0}>
                      <SelectTrigger data-testid="select-policy-topic" className={fieldSelectClassName}>
                        <SelectValue placeholder={availableTopics.length === 0 ? "All topics have been added" : "Select a topic"} />
                      </SelectTrigger>
                      <SelectContent>
                        {availableTopics.map((topic) => (
                          <SelectItem key={topic.id} value={topic.id}>{topic.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-11 rounded-2xl border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-none hover:bg-slate-100"
                      onClick={addTopic}
                      data-testid="button-add-policy-topic"
                      disabled={disabled || !selectedTopicId || availableTopics.length === 0}
                    >
                      <Plus className="h-4 w-4" /> {"Add Topic"}
                    </Button>
                  </div>
                  {policyTopics.length === 0 && <p className="mt-3 text-sm text-muted-foreground">{"No topics added yet. Select a topic and click Add."}</p>}
                </div>

                <div className="space-y-3">
                  {policyTopics.map((topic, index) => {
                    const status = getTopicStatus(topic);
                    const isExpanded = expandedTopicId === topic.id;

                    return (
                      <div key={topic.id} className="overflow-hidden rounded-3xl border border-slate-100 bg-slate-50">
                        <div className="flex items-stretch">
                          <button
                            className="flex flex-1 items-center justify-between p-3 text-left transition-colors hover:bg-slate-100/70"
                            onClick={() => setExpandedTopicId((prev) => prev === topic.id ? null : topic.id)}
                            data-testid={`button-topic-${topic.id}`}
                          >
                            <div className="flex items-center gap-3">
                              {isExpanded ? <ChevronDown className="h-4 w-4 flex-shrink-0 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                              <span className="text-sm font-medium text-slate-900">{index + 1}. {topic.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {topic.policyExists === "yes" && <Badge variant="outline" className="rounded-2xl border-green-200 bg-green-50 text-xs text-green-700 shadow-none">{"Yes"}</Badge>}
                              {topic.policyExists === "no" && <Badge variant="outline" className="rounded-2xl bg-gray-50 text-xs text-gray-600 shadow-none">{"No"}</Badge>}
                              {status === "complete" && <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-600" />}
                              {status === "in-progress" && <AlertCircle className="h-4 w-4 flex-shrink-0 text-amber-500" />}
                              {status === "not-started" && <div className="h-4 w-4 flex-shrink-0 rounded-full border-2 border-muted-foreground/30" />}
                            </div>
                          </button>
                          <div className="border-l border-slate-200">
                            <Button type="button" variant="ghost" size="sm" className="h-full rounded-none px-3 text-destructive shadow-none hover:bg-transparent hover:text-destructive" onClick={() => removeTopic(topic.id)} data-testid={`button-remove-topic-${topic.id}`} disabled={disabled}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="space-y-4 border-t border-slate-200 bg-white p-4">
                            <div className="space-y-2">
                              <Label className="font-medium text-slate-700">{"Is the policy publicly available?"}</Label>
                              <div className="flex gap-2">
                                {(["yes", "no"] as const).map((value) => (
                                  <Button
                                    key={value}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateTopic(topic.id, { publiclyAvailable: value, ...(value !== "yes" ? { publicUrl: "" } : {}) })}
                                    data-testid={`button-publicly-available-${value}`}
                                    disabled={disabled}
                                    className={`${toggleButtonClassName} ${
                                      topic.publiclyAvailable === value
                                        ? "bg-primary text-white hover:bg-[#6e4788]"
                                        : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                                    }`}
                                  >
                                    {value === "yes" ? "Yes" : "No"}
                                  </Button>
                                ))}
                              </div>
                            </div>

                            {topic.publiclyAvailable === "yes" && (
                              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <div className="space-y-2">
                                  <Label className="flex items-center gap-1 text-sm font-medium text-slate-700">
                                    <ExternalLink className="h-3 w-3" /> {"Public URL (optional)"}
                                  </Label>
                                  <Input
                                    type="url"
                                    placeholder={"https://example.com/policy"}
                                    value={topic.publicUrl || ""}
                                    onChange={(e) => updateTopic(topic.id, { publicUrl: e.target.value })}
                                    data-testid="input-public-url"
                                    disabled={disabled}
                                    className={fieldInputClassName}
                                  />
                                  <p className="text-xs text-muted-foreground">{"Provide the URL where the policy can be accessed, or upload the document below."}</p>
                                </div>
                                <DocumentUploadSection
                                  fieldIdentifier={`general-policy-${topic.id}`}
                                  documents={topic.uploadedPolicyFiles || []}
                                  onDocumentsChange={(docs) => updateTopic(topic.id, { uploadedPolicyFiles: docs })}
                                  currentTab="general"
                                  label={"Policy Documents"}
                                  description={"Upload policy documents (PDF, images)"}
                                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                  maxFiles={5}
                                  className="rounded-2xl border border-slate-200 bg-white p-4"
                                  itemClassName="rounded-2xl border border-slate-200 bg-slate-50 p-3"
                                  actionButtonClassName="h-9 rounded-2xl border-slate-200 bg-white px-4 text-sm font-medium text-slate-600 shadow-none hover:bg-slate-100"
                                />
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label className="font-medium text-slate-700">{"Does the policy include specific targets?"}</Label>
                              <div className="flex gap-2">
                                {(["yes", "no"] as const).map((value) => (
                                  <Button
                                    key={value}
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateTopic(topic.id, { hasTargets: value, ...(value !== "yes" ? { targets: [] } : {}) })}
                                    data-testid={`button-has-targets-${value}`}
                                    disabled={disabled}
                                    className={`${toggleButtonClassName} ${
                                      topic.hasTargets === value
                                        ? "bg-primary text-white hover:bg-[#6e4788]"
                                        : "border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100"
                                    }`}
                                  >
                                    {value === "yes" ? "Yes" : "No"}
                                  </Button>
                                ))}
                              </div>
                            </div>

                            {topic.hasTargets === "yes" && (
                              <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                                <div className="flex items-center justify-between">
                                  <Label className="text-sm font-medium text-slate-700">{"Targets"}</Label>
                                  <Button variant="outline" size="sm" onClick={() => addTarget(topic.id)} data-testid="button-add-target" disabled={disabled} className="h-9 rounded-2xl border-slate-200 bg-white px-4 text-sm font-semibold text-slate-900 shadow-none hover:bg-slate-100">
                                    <Plus className="mr-1 h-3 w-3" /> {"Add Target"}
                                  </Button>
                                </div>
                                {(!topic.targets || topic.targets.length === 0) && <p className="text-sm italic text-muted-foreground">{"No targets added yet. Click \"Add Target\" to create one."}</p>}
                                {topic.targets?.map((target, idx) => (
                                  <div key={target.id} className="space-y-3 rounded-2xl border border-slate-200 bg-white p-3">
                                    <div className="flex items-center justify-between">
                                      <span className="text-xs font-medium text-muted-foreground">{`Target ${idx + 1}`}</span>
                                      <Button variant="ghost" size="sm" onClick={() => removeTarget(topic.id, target.id)} className="h-6 w-6 p-0 text-destructive shadow-none hover:bg-transparent hover:text-destructive" disabled={disabled}>
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div className="space-y-1">
                                        <Label className="text-xs">{"Target Name"} <span className="text-red-500">*</span></Label>
                                        <Input className={fieldInputClassName} placeholder={"e.g. Reduce emissions"} value={target.targetName} onChange={(e) => updateTarget(topic.id, target.id, { targetName: e.target.value })} disabled={disabled} />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">{"Metric"} <span className="text-red-500">*</span></Label>
                                        <Input className={fieldInputClassName} placeholder={"e.g. tCO2e, kWh, %"} value={target.metric} onChange={(e) => updateTarget(topic.id, target.id, { metric: e.target.value })} disabled={disabled} />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">{"Target Year"} <span className="text-red-500">*</span></Label>
                                        <Input className={fieldInputClassName} type="number" placeholder="2030" value={target.targetYear || ""} onChange={(e) => updateTarget(topic.id, target.id, { targetYear: parseInt(e.target.value) || 0 })} disabled={disabled} />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">{"Target Value"} <span className="text-red-500">*</span></Label>
                                        <Input className={fieldInputClassName} type="number" placeholder="0" value={target.targetValue || ""} onChange={(e) => updateTarget(topic.id, target.id, { targetValue: parseFloat(e.target.value) || 0 })} disabled={disabled} />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">{"Baseline Year"}</Label>
                                        <Input className={fieldInputClassName} type="number" placeholder="2024" value={target.baselineYear || ""} onChange={(e) => updateTarget(topic.id, target.id, { baselineYear: parseInt(e.target.value) || undefined })} disabled={disabled} />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">{"Baseline Value"}</Label>
                                        <Input className={fieldInputClassName} type="number" placeholder={"Current value"} value={target.baselineValue || ""} onChange={(e) => updateTarget(topic.id, target.id, { baselineValue: parseFloat(e.target.value) || undefined })} disabled={disabled} />
                                      </div>
                                    </div>
                                    <div className="space-y-1">
                                      <Label className="text-xs">{"Scope"}</Label>
                                      <Select value={target.scope || ""} onValueChange={(value) => updateTarget(topic.id, target.id, { scope: value as any })} disabled={disabled}>
                                        <SelectTrigger className={fieldSelectClassName}><SelectValue placeholder={"Select scope"} /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="company-wide">{"Company-wide"}</SelectItem>
                                          <SelectItem value="business-unit">{"Business Unit"}</SelectItem>
                                          <SelectItem value="site">{"Site"}</SelectItem>
                                          <SelectItem value="product-line">{"Product Line"}</SelectItem>
                                          <SelectItem value="other">{"Other"}</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-slate-700">{"Notes (optional)"}</Label>
                              <Textarea
                                placeholder={"Additional context or notes about this topic..."}
                                value={topic.notes || ""}
                                onChange={(e) => updateTopic(topic.id, { notes: e.target.value })}
                                className="min-h-[80px] rounded-3xl border-0 bg-slate-50 px-4 pt-3 text-sm text-slate-900 outline outline-1 outline-offset-[-1px] outline-slate-200 placeholder:text-slate-400 focus-visible:ring-0"
                                data-testid="textarea-topic-notes"
                                disabled={disabled}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="text-sm font-medium text-slate-700">{"Internal Owner"}</Label>
                              <Input
                                placeholder={"Select internal owner"}
                                value={topic.internalOwner || ""}
                                onChange={(e) => updateTopic(topic.id, { internalOwner: e.target.value })}
                                data-testid="input-internal-owner"
                                disabled={disabled}
                                className={fieldInputClassName}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <DocumentUploadSection
                fieldIdentifier="general-sustainability-policy"
                documents={sustainabilityPolicyDocuments}
                onDocumentsChange={setSustainabilityPolicyDocuments}
                currentTab="general"
                label={"General Policy Documents"}
                description={"Upload overall policy documents, codes of conduct, or ESG policies"}
                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                maxFiles={10}
                className="rounded-2xl border-2 border-slate-200 bg-slate-50 px-6 py-8"
                itemClassName="rounded-2xl border border-slate-200 bg-white p-3"
                actionButtonClassName="h-10 rounded-2xl bg-primary px-4 text-xs font-bold text-white shadow-none hover:bg-[#6e4788]"
              />

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <h4 className={sectionTitleClassName}>{"Sustainability Integration Description"}</h4>
                    <Info className="h-4 w-4 text-slate-600" />
                  </div>
                  <VoiceButton
                    onTranscript={setSustainabilityPolicyDescription}
                    disabled={disabled}
                    variant="ghost"
                    className="h-7 rounded-2xl px-0 text-xs font-semibold text-slate-500 shadow-none hover:text-[#00444C]"
                    recordingClassName="text-destructive hover:text-destructive"
                    data-testid="button-voice-policy-description"
                  />
                </div>
                <p className={sectionCopyClassName}>Briefly explain how sustainability is managed in your business. For example: who is responsible, what actions are taken, and how progress is tracked.</p>
                <Textarea
                  placeholder={"Select integration"}
                  className="min-h-[160px] rounded-3xl border-0 bg-slate-50 px-4 pt-3 text-sm text-slate-900 outline outline-1 outline-offset-[-1px] outline-slate-200 placeholder:text-slate-400 focus-visible:ring-0"
                  value={sustainabilityPolicyDescription}
                  onChange={(e) => setSustainabilityPolicyDescription(e.target.value)}
                  data-testid="textarea-policy-description"
                  disabled={disabled}
                />
                <p className="text-xs text-muted-foreground">This information will be used in the next section, so you don&apos;t need to enter it again.</p>
              </div>
            </div>
          )}
        </div>
      </SectionCard>

      <ProgressCard completion={sustainabilityCardCompletion} />
    </section>
  );
}
